import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTablePage, { DataTableColumn } from 'components/DataTablePage';
import { Project, User } from 'types';
import apiReq from 'core/api';
import { useSnackbar } from 'components/GlobalSnackbar';
import { formatUnixTimestamp } from 'utils/dateUtils';
import { useDispatch, useSelector } from 'react-redux';
import { setProjects } from '../../redux/reducers';
import store from '../../redux/store';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projects: Project[] = useSelector(
    (state: ReturnType<typeof store.getState>) => state.reducer.projects
  );
  const [isLoading, setIsLoading] = useState(true);
  const showSnackbar = useSnackbar();

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiReq<Project[]>('projects');
      dispatch(setProjects(data));
    } catch (error) {
      showSnackbar({ message: 'Ошибка загрузки проектов', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, showSnackbar]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreateProject = () => {
    navigate('/projects/create');
  };

  const handleEditProject = (project: Project) => {
    navigate(`/projects/edit/${project.id}`);
  };

  const handleDeleteProject = async (project: Project) => {
    try {
      const response = await apiReq<{ status: string }>(`project/${project.id}/delete`, 'DELETE');

      if (response?.status === 'success') {
        showSnackbar({ message: 'Проект успешно удален', severity: 'success' });
        loadProjects();
      } else {
        showSnackbar({ message: 'Ошибка удаления проекта', severity: 'error' });
      }
    } catch (error) {
      if (error instanceof Error && 'message' in error) {
        showSnackbar({ message: error.message, severity: 'error' });
      } else {
        showSnackbar({ message: 'Ошибка удаления проекта', severity: 'error' });
      }
    }
  };

  const columns: DataTableColumn[] = [
    {
      key: 'name',
      label: 'Название',
      sortable: true,
      sortFn: (a: unknown, b: unknown) => String(a ?? '').localeCompare(String(b ?? ''), 'ru'),
    },
    {
      key: 'created_at',
      label: 'Дата создания',
      sortable: true,
      sortFn: (a: unknown, b: unknown) => {
        const aValue = typeof a === 'number' ? a : typeof a === 'string' ? Number(a) : 0;
        const bValue = typeof b === 'number' ? b : typeof b === 'string' ? Number(b) : 0;
        return aValue - bValue;
      },
      render: (value: unknown) => formatUnixTimestamp(value as number | string),
    },
    {
      key: 'last_activity_at',
      label: 'Последняя активность',
      sortable: true,
      sortFn: (a: unknown, b: unknown) => {
        const aValue = typeof a === 'number' ? a : typeof a === 'string' ? Number(a) : 0;
        const bValue = typeof b === 'number' ? b : typeof b === 'string' ? Number(b) : 0;
        return aValue - bValue;
      },
      render: (value: unknown) => (value ? formatUnixTimestamp(value as number | string) : ''),
    },
    {
      key: 'users',
      label: 'Количество пользователей',
      sortable: true,
      sortFn: (a: unknown, b: unknown) => {
        const aLength = Array.isArray(a) ? a.length : 0;
        const bLength = Array.isArray(b) ? b.length : 0;
        return aLength - bLength;
      },
      render: (value) => `${(value as User[]).length} чел.`,
    },
  ];

  return (
    <DataTablePage<Project>
      title="Проекты"
      columns={columns}
      data={projects}
      isLoading={isLoading}
      onCreate={handleCreateProject}
      onEdit={handleEditProject}
      onDelete={handleDeleteProject}
      createButtonText="Создать проект"
      emptyMessage="Проекты не найдены"
    />
  );
};

export default ProjectsPage;
